import React from "react";
import useSWR from "swr";
import axios from "axios";
import { Link } from "react-router";
import Select from "react-select-new";
import moment from "moment";

import { fetcher } from "../../components/Overview/Overview";
import { downloadFile } from "../../functions";
import FormErrorHandler from "../../components/FormErrorHandler";

function fullName(user) {
    if (user) {
        return (
            user.full_name ||
            ["first_name", "last_name_prefix", "last_name"]
                .map((key) => user[key])
                .filter(Boolean)
                .join(" ")
        );
    }
    return "";
}

export default function AuditDetail({ params, router }) {
    const { uuid } = params;
    const { data: event, mutate: mutateEvent } = useSWR(() => `/v1/audits/${uuid}/`, fetcher);
    const { data: documents, mutate: mutateDocuments } = useSWR(() => `/v1/audits/${uuid}/eventroles/${event.event_roles[0].uuid}/documents/`, fetcher);

    const blankEditDocument = {
        uuid: "",
        name: "",
        description: "",
        is_published: false
    };
    const [editDocument, setEditDocument] = React.useState({...blankEditDocument});
    const [errors, setErrors] = React.useState({});

    const auditors = {};
    if (event) {
        event.event_roles.forEach((role) => {
            if (role.auditor) {
                auditors[role.auditor.uuid] = role.auditor;
            }
        });
    }

    const auditStateChoices = [
        { value: "", label: "Niet gecontroleerd" },
        // Audited, no abnormalities:
        { value: "no-abnormalities", label: "Gecontroleerd, geen afwijkingen" },
        // Audited, abnormalities:
        { value: "abnormalities", label: "Gecontroleerd, afwijkingen" },
        // Good with advice
        { value: "good-with-advice", label: "Goed met advies" }
    ];

    const handleAuditStateChange = (value) => {
        axios
            .patch(`/v1/audits/${uuid}/eventroles/${event.event_roles[0].uuid}/`, {
                audit_state: value
            })
            .then((r) => mutateEvent());
    };

    const handleFormSave = (e) => {
        e.preventDefault();
        const form = e.target;
        const data = new FormData(form);

        let url = `/v1/audits/${uuid}/eventroles/${event.event_roles[0].uuid}/documents/`;
        let method = "POST";
        if (editDocument.uuid) {
            // we are editing:
            url += `${editDocument.uuid}/`;
            method = "PATCH";
            // file isn't required when editing, but it will error if we don't remove it
            const fileData = data.get("file");
            if (!fileData || fileData.size === 0) {
                data.delete("file");
            }
            // ensure is_published = false when not checked
            if (!data.get("is_published")) {
                data.set("is_published", "0");
            }
        }
        axios(url, {method, data}).then((response) => {
            setEditDocument(blankEditDocument);
            mutateDocuments();
            form.reset();
        }).catch((error) => {
            if (error.response.status === 400) {
                return setErrors(error.response.data);
            }
            console.warn({error});
        });
    };

    const handleEditDocumentFieldChange = (e) => {
        const { name, value } = e.target;
        setEditDocument((prev) => ({ ...prev, [name]: value }));
    };
    const handleEditDocumentCheckboxToggle = (e) => {
        const { name } = e.target;
        setEditDocument((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const DATETIME_FORMAT = "DD-MM-YYYY HH:mm";

    return (
        <>
            <div className="section-top">
                <div className="breadcrumbs">
                    <Link to="/audits">Controles</Link>
                </div>
                <h1>
                    <strong>Controle</strong>
                </h1>
            </div>
            <div className="row">
                <div className="col-12 filters mb-2">
                    <h2>{event?.id}</h2>
                </div>
                <div className="col-9">
                    {event ? (
                        <>
                            <div className="row mb-2">
                                <div className="col-4">Cursus</div>
                                <div className="col-8">
                                    <strong
                                        className="link"
                                        onClick={() => {
                                            router.push("/courses/edit/" + event.course.uuid + "/students");
                                        }}
                                    >
                                        {event.course.id}
                                    </strong>
                                </div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-4">Toetsmoment</div>
                                <div className="col-8">
                                    <strong>{event.name}</strong>
                                </div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-4">Opleider</div>
                                <div className="col-8">
                                    <strong>{event.trainer.name}</strong>
                                </div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-4">Datum</div>
                                <div className="col-8">
                                    <strong>{moment(event.start_datetime).format(DATETIME_FORMAT)}</strong>
                                </div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-4">{Object.keys(auditors).length === 1 ? "Controleur" : "Controleurs"}</div>
                                <div className="col-8">
                                    <strong>
                                        {Object.values(auditors).map((auditor) => (
                                            <React.Fragment key={auditor.uuid}>
                                                {auditor.full_name}
                                                <br />
                                            </React.Fragment>
                                        ))}
                                    </strong>
                                </div>
                            </div>
                            <div className="row mb-2">
                                <div style={{ width: "18em" }} className="col-4">
                                    Controle status
                                </div>
                                <div className="col-8">
                                    <Select
                                        name="audit_state"
                                        defaultValue={auditStateChoices.filter((option) => option.value === (event.event_roles[0].audit_state || ""))}
                                        options={auditStateChoices}
                                        placeholder="Selecteer een status"
                                        onChange={(option) => handleAuditStateChange(option.value)}
                                        controlShouldRenderValue
                                    />
                                </div>
                            </div>

                            <div className="row mb-2">
                                <div className="col-4">Laatst aangepast door</div>
                                <div className="col-8">
                                    <strong>{fullName(event.event_roles[0].audit_state_changed_by) || "-"}</strong>
                                </div>
                            </div>

                            <div className="row mb-2">
                                <div className="col-4">Laatst aangepast op</div>
                                <div className="col-8">
                                    <strong>{event.event_roles[0].audit_state_changed_on ? moment(event.event_roles[0].audit_state_changed_on).format(DATETIME_FORMAT) : "-"}</strong>
                                </div>
                            </div>

                            {documents ? (
                                documents.results.length ? (
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>Document</th>
                                                <th>Gepubliceerd op</th>
                                                <th width="180">Ge&uuml;pload op</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {documents.results.map((doc) => (
                                                <tr key={doc.uuid}>
                                                    <td>
                                                        <button
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                downloadFile(doc.url);
                                                            }}
                                                            className="link"
                                                        >
                                                            {doc.name}
                                                            <i className="fa fa-download" />
                                                        </button>
                                                        <div className="lighter">{doc.description}</div>
                                                    </td>
                                                    <td>{doc.published ? moment(doc.published).format(DATETIME_FORMAT) : "-"}</td>
                                                    <td>{moment(doc.created).format(DATETIME_FORMAT)}</td>
                                                    <td align="right">
                                                        {doc.uuid !== editDocument.uuid ? (
                                                            <button onClick={() => setEditDocument(doc)} className="link">bewerk <i className="fa fa-pencil" /></button>
                                                        ) : (
                                                            <button onClick={() => setEditDocument(blankEditDocument)} className="link">annuleer <i className="fa fa-times" /></button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : null
                            ) : (
                                <div className="spinner-border spinner-border-sm float-right" role="status">
                                    <span className="sr-only">Bezig met laden...</span>
                                </div>
                            )}

                            <form className="mt-5" onSubmit={handleFormSave}>
                                <h2>{editDocument.uuid ? "Bewerk document" : "Voeg een document toe"}</h2>
                                <div className="form-group">
                                    <label htmlFor="name">Naam</label>
                                    <input type="text" className="form-control" name="name" id="name" placeholder="Naam" onChange={handleEditDocumentFieldChange} value={editDocument.name} />
                                    <FormErrorHandler errorObject={errors} fieldName="name" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="description">Beschrijving</label>
                                    <textarea className="form-control" name="description" id="description" rows="3" placeholder="Beschrijving" onChange={handleEditDocumentFieldChange} value={editDocument.description} />
                                    <FormErrorHandler errorObject={errors} fieldName="description" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="file">Bestand</label>
                                    <input type="file" name="file" className="form-control-file" id="file" />
                                    {editDocument.uuid && (<small className="form-text text-muted">
                                        Selecteer een nieuw bestand om het te vervangen.
                                    </small>)}
                                    <FormErrorHandler errorObject={errors} fieldName="file" />
                                </div>
                                <div className="form-group">
                                    <p className="custom-control custom-checkbox my-3">
                                        <input
                                            type="checkbox"
                                            name="is_published"
                                            value="1"
                                            className="custom-control-input"
                                            id="is_published"
                                            onChange={handleEditDocumentCheckboxToggle}
                                            checked={!!editDocument.is_published}
                                        />
                                        <label className="custom-control-label" htmlFor="is_published">
                                            Publiceren?
                                        </label>
                                        <br />
                                        <small className="form-text text-muted">
                                            Alleen wanneer deze optie is aangevinkt is het document zichtbaar voor de opleider.
                                        </small>
                                    </p>
                                    <FormErrorHandler errorObject={errors} fieldName="is_published" />
                                </div>
                                <button type="submit" className="btn btn-primary">
                                    Opslaan
                                </button>
                                {editDocument.uuid && (
                                    <>
                                        <input type="hidden" name="uuid" value={editDocument.uuid} />
                                        <button type="button" className="btn btn-secondary" onClick={(e) => {
                                            e.preventDefault();
                                            setEditDocument(blankEditDocument);
                                        }}>Annuleren</button>
                                    </>
                                )}
                            </form>
                        </>
                    ) : (
                        <div className="spinner-border spinner-border-sm float-right" role="status">
                            <span className="sr-only">Bezig met laden...</span>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
